import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { siteBrandName } from '../../content/navigation';
import { useAuth } from '../../hooks/use-auth';
import { useAppShellSites } from './app-shell-context';

type AppSidebarProps = {
  siteId?: string | null;
  onUndo?: () => void;
  onToggleHistory?: () => void;
};

function NavLink({
  to,
  label,
  currentPath,
  exact = false,
  icon,
}: {
  to: string;
  label: string;
  currentPath: string;
  exact?: boolean;
  icon?: React.ReactNode;
}) {
  const isActive = exact
    ? currentPath === to
    : currentPath === to || currentPath.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
        isActive
          ? 'text-primary font-medium bg-background'
          : 'text-textMuted hover:text-primary hover:bg-background'
      }`}
    >
      {icon && <span className="shrink-0 w-4 h-4">{icon}</span>}
      {label}
    </Link>
  );
}

const iconClass = "w-4 h-4";

const icons = {
  editor: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  ),
  theme: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="10.5" r="2.5" />
      <circle cx="8.5" cy="7.5" r="2.5" />
      <circle cx="6.5" cy="12.5" r="2.5" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.38-.15-.74-.39-1.04-.24-.3-.39-.66-.39-1.04 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.17-4.5-9-10-9Z" />
    </svg>
  ),
  assets: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" />
    </svg>
  ),
  forms: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 7h10" />
      <path d="M7 12h10" />
      <path d="M7 17h6" />
    </svg>
  ),
  analytics: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="m7 16 4-8 4 5 5-6" />
    </svg>
  ),
  publish: (
    <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
    </svg>
  ),
};

function SiteDropdown({
  sites,
  siteId,
  onSiteChange,
}: {
  sites: { id: string; name: string | null; slug: string }[];
  siteId?: string | null;
  onSiteChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = sites.find((s) => s.id === siteId);
  const label = selected
    ? selected.name || selected.slug || `site-${selected.id.slice(0, 8)}`
    : 'サイトを選択';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  return (
    <div ref={ref} className="relative px-1 mb-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm rounded-sm border border-border bg-background text-primary hover:bg-surfaceMuted transition-colors"
      >
        <span className="truncate">{label}</span>
        <svg
          className={`shrink-0 w-4 h-4 text-textMuted transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 mx-0 bg-background border border-border rounded-sm shadow-md overflow-hidden">
          {sites.map((s) => {
            const name = s.name || s.slug || `site-${s.id.slice(0, 8)}`;
            const isActive = s.id === siteId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  onSiteChange(s.id);
                  setOpen(false);
                }}
                className={`flex items-center w-full px-3 py-2 text-sm text-left transition-colors ${
                  isActive
                    ? 'bg-surfaceMuted text-primary font-medium'
                    : 'text-secondary hover:bg-surface'
                }`}
              >
                {isActive && (
                  <svg className="shrink-0 w-3.5 h-3.5 mr-2 text-accent" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8.5l3.5 3.5 6.5-8" />
                  </svg>
                )}
                {!isActive && <span className="w-3.5 mr-2 shrink-0" />}
                <span className="truncate">{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppSidebar({
  siteId,
}: AppSidebarProps) {
  const { signOut } = useAuth();
  const { sites } = useAppShellSites();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const hasSite = Boolean(siteId);

  function handleSiteChange(nextSiteId: string) {
    if (!nextSiteId || nextSiteId === siteId) return;
    navigate(`/app/editor/${nextSiteId}`);
  }

  return (
    <aside className="w-full shrink-0 border-b border-border bg-surface px-4 py-5 flex flex-col md:w-60 md:border-b-0 md:border-r md:h-dvh md:overflow-hidden">
      {/* ブランド */}
      <Link to="/app" className="inline-flex items-center px-2 mb-4">
        <span className="text-xl font-bold text-primary">{siteBrandName}</span>
      </Link>

      {/* サイト切替プルダウン */}
      {sites.length > 0 && (
        <SiteDropdown
          sites={sites}
          siteId={siteId}
          onSiteChange={handleSiteChange}
        />
      )}

      <hr className="border-border mb-3" />

      <nav className="flex-1 flex flex-col overflow-y-auto">
        {hasSite ? (
          <>
            {/* エディタ */}
            <div className="space-y-0.5">
              <NavLink to={`/app/editor/${siteId}`} label="エディタ" currentPath={currentPath} icon={icons.editor} />
            </div>

            {/* 設定グループ */}
            <div className="mt-4">
              <p className="text-[11px] font-medium text-textMuted/60 uppercase tracking-wider px-3 mb-1.5">設定</p>
              <div className="space-y-0.5">
                <NavLink to={`/app/theme/${siteId}`} label="詳細設定" currentPath={currentPath} icon={icons.theme} />
                <NavLink to={`/app/assets/${siteId}`} label="素材管理" currentPath={currentPath} icon={icons.assets} />
                <NavLink to={`/app/forms/${siteId}`} label="フォーム設定" currentPath={currentPath} icon={icons.forms} />
              </div>
            </div>

            <hr className="border-border my-4" />

            {/* アクセス状況・公開設定 */}
            <div className="space-y-0.5">
              <NavLink to={`/app/analytics/${siteId}`} label="アクセス状況" currentPath={currentPath} icon={icons.analytics} />
              <NavLink to={`/app/publish/${siteId}`} label="公開設定" currentPath={currentPath} icon={icons.publish} />
            </div>
          </>
        ) : null}

        {/* 共通ナビ */}
        <div className={`${hasSite ? 'mt-auto' : 'mt-0'} pt-4 border-t border-border space-y-0.5`}>
          <NavLink to="/app" label="サイト一覧" currentPath={currentPath} exact />
          <NavLink to="/app/wizard" label="新規サイト作成" currentPath={currentPath} />
          <NavLink to="/app/templates" label="テンプレート" currentPath={currentPath} />
          <NavLink to="/app/template-catalog" label="テンプレートカタログ" currentPath={currentPath} />
          <NavLink to="/app/billing" label="プランと請求" currentPath={currentPath} />
        </div>
      </nav>

      {/* ログアウト */}
      <hr className="border-border my-2" />
      <button
        type="button"
        className="block w-full text-left px-3 py-2 rounded-sm text-sm text-textMuted hover:text-primary hover:bg-background transition-colors"
        onClick={() => void signOut()}
      >
        ログアウト
      </button>
    </aside>
  );
}
